// =============================================================================
// chat-client.http.ts — Adaptateur HTTP RÉEL du ChatClient (R15-COM-02, DEC-31).
// =============================================================================
// Une SEULE classe paramétrée par le PROFIL (admin / supplier) : le préfixe de base et le cookie CSRF
// double-submit sont figés à la construction (jamais choisis par l'appelant). Le montant est 100% REST.
//   - ADMIN      → base `/api/communication/admin`, CSRF `admin_csrf`    (parité catalog-edition/reminders) ;
//   - FOURNISSEUR → base `/api/communication`,       CSRF `supplier_csrf` (parité supplier-team).
//
// SÉCURITÉ : le secret de session (cookie httpOnly) n'est JAMAIS lu ici — porté par `credentials:'include'`.
// Les LECTURES (GET) n'exigent AUCUN CSRF. Les MUTATIONS (POST) portent l'en-tête double-submit
// `x-csrf-token` = cookie non-httpOnly du profil ; sans lui, l'API répond 403 (fail-closed serveur). Le
// siret/contactId/subject viennent de la SESSION côté API (jamais du body). MAPPING ERREURS par STATUS
// HTTP (401/403/404, message serveur relayé). Le CONTENU des messages est REST-only (PII, RLS).

import {
  ChatApiError,
  type ChatApiErrorKind,
  type ChatClient,
  type ChatProfile,
  type MarkReadResultDto,
  type SendMessageResultDto,
  type ThreadFilters,
  type ThreadMessagesDto,
  type ThreadsListDto,
} from '@/api/chat-client';

/** Forme du corps d'erreur NestJS (HttpException standard) — message éventuellement string[]. */
interface NestErrorBody {
  readonly statusCode?: number;
  readonly message?: string | readonly string[];
  readonly error?: string;
}

/** Mapping STATUS HTTP → kind UX (401 session, 403 garde/CSRF, 404 ressource). */
const STATUS_TO_KIND: Readonly<Record<number, ChatApiErrorKind>> = {
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not-found',
};

/** Configuration figée par profil : préfixe de base (sous `/api`) + nom du cookie CSRF double-submit. */
interface ChatProfileConfig {
  readonly pathPrefix: string;
  readonly csrfCookieName: string;
}

const PROFILE_CONFIG: Readonly<Record<ChatProfile, ChatProfileConfig>> = {
  admin: { pathPrefix: 'communication/admin', csrfCookieName: 'admin_csrf' },
  supplier: { pathPrefix: 'communication', csrfCookieName: 'supplier_csrf' },
};

/**
 * Lit la valeur d'un cookie NON-httpOnly (lisible par le SPA même origine). `undefined` si absent (pas
 * de session, ou contexte sans `document`) — l'appel mutant partira alors sans en-tête et l'API répondra
 * 403 (fail-closed serveur, jamais d'écriture). Parité stricte des adaptateurs existants.
 */
function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  for (const part of document.cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (rawName === name) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
}

/**
 * Adaptateur HTTP réel : appels `fetch` contre l'API chat. `baseUrl` par défaut `/api` (SPA et API
 * partagent l'origine, ou un reverse proxy route `/api/*`). Le `profile` FIGE le préfixe et le cookie CSRF.
 */
export class HttpChatClient implements ChatClient {
  private readonly config: ChatProfileConfig;

  public constructor(
    private readonly profile: ChatProfile,
    private readonly baseUrl: string = '/api',
  ) {
    this.config = PROFILE_CONFIG[profile];
  }

  /** Construit l'URL absolue (baseUrl + préfixe de profil + chemin relatif). */
  private url(path: string): string {
    return `${this.baseUrl}/${this.config.pathPrefix}${path}`;
  }

  public async listThreads(filters: ThreadFilters): Promise<ThreadsListDto> {
    const params = new URLSearchParams();
    if (filters.campaignId !== undefined && filters.campaignId.length > 0) {
      params.set('campaignId', filters.campaignId);
    }
    if (filters.unreadOnly === true) {
      params.set('unreadOnly', 'true');
    }
    // `q` (recherche SIRET) n'a de sens QUE pour l'admin — ignoré côté supplier (fail-safe, jamais émis).
    if (
      this.profile === 'admin' &&
      filters.q !== undefined &&
      filters.q.length > 0
    ) {
      params.set('q', filters.q);
    }
    const query = params.toString();
    return this.get<ThreadsListDto>(`/threads${query.length > 0 ? `?${query}` : ''}`);
  }

  public getMessages(threadId: string): Promise<ThreadMessagesDto> {
    return this.get<ThreadMessagesDto>(
      `/threads/${encodeURIComponent(threadId)}/messages`,
    );
  }

  public sendMessage(
    threadId: string,
    content: string,
  ): Promise<SendMessageResultDto> {
    // Corps `{ content }` UNIQUEMENT : aucun destinataire/siret/sender_ref client (autorité = session API).
    return this.mutate<SendMessageResultDto>(
      `/threads/${encodeURIComponent(threadId)}/messages`,
      JSON.stringify({ content }),
    );
  }

  public markRead(threadId: string): Promise<MarkReadResultDto> {
    return this.mutate<MarkReadResultDto>(
      `/threads/${encodeURIComponent(threadId)}/read`,
      undefined,
    );
  }

  /** Exécute un GET (cookie de session, aucun CSRF) et renvoie le JSON typé, ou lève une erreur mappée. */
  private async get<T>(path: string): Promise<T> {
    const response = await fetch(this.url(path), {
      method: 'GET',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as T;
  }

  /**
   * Exécute une MUTATION (POST) avec en-tête CSRF double-submit (cookie non-httpOnly du profil). Sans le
   * cookie, l'en-tête est omis et l'API répond 403 (fail-closed serveur). Renvoie le JSON typé, ou lève.
   */
  private async mutate<T>(path: string, body: string | undefined): Promise<T> {
    const headers: Record<string, string> = {
      'content-type': 'application/json',
    };
    const csrf = readCookie(this.config.csrfCookieName);
    if (csrf !== undefined) {
      headers['x-csrf-token'] = csrf;
    }
    const response = await fetch(this.url(path), {
      method: 'POST',
      credentials: 'include',
      headers,
      ...(body !== undefined ? { body } : {}),
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as T;
  }

  /** Convertit une réponse non-OK en erreur applicative typée (message serveur relayé + kind par status). */
  private async toApiError(response: Response): Promise<ChatApiError> {
    const body = await this.safeJson(response);
    const message =
      this.extractMessage(body) ?? `Erreur API (${String(response.status)}).`;
    const kind = STATUS_TO_KIND[response.status] ?? 'generic';
    return new ChatApiError(message, kind);
  }

  private extractMessage(body: NestErrorBody | undefined): string | undefined {
    const message = body?.message;
    if (message === undefined) {
      return undefined;
    }
    return typeof message === 'string' ? message : message.join(' ');
  }

  private async safeJson(response: Response): Promise<NestErrorBody | undefined> {
    try {
      return (await response.json()) as NestErrorBody;
    } catch {
      return undefined;
    }
  }
}
